import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Shared ref so multiple setters called in the same tick
 * build on top of each other instead of overwriting.
 */
let pendingParams: URLSearchParams | null = null;

/**
 * Like useState, but synced with a URL query parameter.
 *
 * - Reads initial value from the URL (falls back to `defaultValue`)
 * - Updates the URL on change (uses `replace` to avoid history spam)
 * - Removes the param when set back to `defaultValue` to keep URLs clean
 * - Preserves all other existing query params
 * - Multiple setters in the same tick are batched correctly
 *
 * @example
 * const [tab, setTab] = useQueryParam('tab', 'overview');
 */
export function useQueryParam(
	key: string,
	defaultValue: string
): [string, (value: string) => void] {
	const [searchParams, setSearchParams] = useSearchParams();
	const value = searchParams.get(key) || defaultValue;

	const setValue = useCallback(
		(newValue: string) => {
			setSearchParams(
				(prev) => {
					// Use pending params from a previous setter in this tick,
					// otherwise start from the current URL params
					const next = new URLSearchParams(pendingParams || prev);
					if (newValue === defaultValue) {
						next.delete(key);
					} else {
						next.set(key, newValue);
					}
					pendingParams = next;

					// Clear after the current tick so the next event starts fresh
					queueMicrotask(() => {
						pendingParams = null;
					});

					return next;
				},
				{ replace: true }
			);
		},
		[key, defaultValue, setSearchParams]
	);

	return [value, setValue];
}

/**
 * Like useQueryParam, but for integer values.
 *
 * @example
 * const [page, setPage] = useQueryParamInt('page', 1);
 */
export function useQueryParamInt(
	key: string,
	defaultValue: number
): [number, (value: number) => void] {
	const [str, setStr] = useQueryParam(key, String(defaultValue));
	const value = parseInt(str, 10) || defaultValue;

	const setValue = useCallback((v: number) => setStr(String(v)), [setStr]);

	return [value, setValue];
}

/**
 * Like useQueryParam, but for a multi-select filter.
 *
 * Serialised comma-separated, which keeps the URL readable and shareable —
 * `?clients=abc,def`. Ids here are opaque strings with no commas in them; if
 * that ever stops being true this needs a real encoding.
 *
 * @example
 * const [clients, setClients] = useQueryParamList('clients');
 */
export function useQueryParamList(key: string): [string[], (value: string[]) => void] {
	const [raw, setRaw] = useQueryParam(key, '');

	const value = useMemo(() => (raw ? raw.split(',').filter(Boolean) : []), [raw]);

	const setValue = useCallback(
		(next: string[]) => setRaw(next.filter(Boolean).join(',')),
		[setRaw]
	);

	return [value, setValue];
}

/**
 * Like useQueryParam, but for a date filter.
 *
 * Stored as `YYYY-MM-DD` rather than an ISO timestamp: these are calendar
 * bounds a person picked, and a timestamp in the URL would shift the filter
 * across timezones and make the link mean something different to whoever opens
 * it.
 */
export function useQueryParamDate(key: string): [Date | null, (value: Date | null) => void] {
	const [raw, setRaw] = useQueryParam(key, '');

	const value = useMemo(() => {
		if (!raw) return null;
		const parsed = new Date(`${raw}T00:00:00`);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}, [raw]);

	const setValue = useCallback(
		(next: Date | null) => {
			if (!next) {
				setRaw('');
				return;
			}
			const month = String(next.getMonth() + 1).padStart(2, '0');
			const day = String(next.getDate()).padStart(2, '0');
			setRaw(`${next.getFullYear()}-${month}-${day}`);
		},
		[setRaw]
	);

	return [value, setValue];
}
